import { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, IconButton, useTheme } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * DropPhoto - A customizable drag and drop image upload component with thumbnails
 * 
 * @param {Object} props
 * @param {boolean} props.multiple - Allow multiple image uploads
 * @param {function} props.onChange - Callback when images change (images, event)
 * @param {function} props.onDrop - Callback when images are dropped
 * @param {number} props.maxFileSize - Maximum file size in bytes
 * @param {number} props.maxFiles - Maximum number of images allowed when multiple is true
 * @param {Object} props.sx - Custom styling for the container
 * @param {string} props.dropzoneText - Custom text for the dropzone
 * @param {string} props.buttonText - Custom text for the upload button
 * @param {boolean} props.base64 - Whether to convert images to base64
 * @returns {JSX.Element}
 */
export default function DropPhoto({
    multiple = false,
    onChange,
    onDrop,
    maxFileSize = 20 * 1024 * 1024, // 20MB default
    maxFiles = 5,
    sx = {},
    dropzoneText = "Arrastra y suelta imágenes aquí",
    buttonText = "Seleccionar imágenes",
    base64 = false
}) {
    const [images, setImages] = useState([]);
    const [isDragActive, setIsDragActive] = useState(false);
    const [errors, setErrors] = useState([]);
    
    const fileInputRef = useRef(null);
    const theme = useTheme();

    // Clear previous errors when images change
    useEffect(() => {
        setErrors([]);
    }, [images]);

    // Convert image to base64
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        processFiles(selectedFiles, event);
        // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
        event.target.value = '';
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        
        const droppedFiles = Array.from(event.dataTransfer.files);
        processFiles(droppedFiles, event);
        
        if (onDrop) {
            onDrop(droppedFiles, event);
        }
    };

    const processFiles = async (newFiles, event) => {
        const newErrors = [];
        
        // Check max files limit
        if (multiple && images.length + newFiles.length > maxFiles) {
            newErrors.push(`No puedes subir más de ${maxFiles} imágenes.`);
            setErrors(newErrors);
            return;
        }
        
        // Validate each file
        const validFiles = newFiles.filter(file => {
            // Check if file is an image
            if (!file.type.startsWith('image/')) {
                newErrors.push(`El archivo ${file.name} no es una imagen válida.`);
                return false;
            }
            
            // Check file size
            if (maxFileSize && file.size > maxFileSize) {
                const sizeMB = (maxFileSize / (1024 * 1024)).toFixed(2);
                newErrors.push(`Imagen demasiado grande: ${file.name}. Máximo: ${sizeMB} MB`);
                return false;
            }
            
            return true;
        });
        
        setErrors(newErrors);
        
        if (validFiles.length > 0) {
            // Limpiar las URLs de preview anteriores si no estamos en modo múltiple
            if (!multiple) {
                images.forEach(image => URL.revokeObjectURL(image.preview));
            }

            let updatedImages;
            
            if (multiple) {
                // En modo múltiple, siempre agregamos al array existente
                updatedImages = [...images, ...validFiles];
            } else {
                // En modo single, reemplazamos el array con la nueva imagen
                updatedImages = validFiles.slice(0, 1);
            }

            try {
                if (base64) {
                    const convertedImages = await Promise.all(updatedImages.map(async (image) => {
                        // Si la imagen ya tiene preview, la mantenemos
                        if (image.preview) {
                            return image;
                        }
                        // Si es una nueva imagen, la convertimos
                        const base64String = await convertToBase64(image);
                        return {
                            name: image.name,
                            size: image.size,
                            type: image.type,
                            lastModified: image.lastModified,
                            file: image,
                            base64: base64String,
                            preview: URL.createObjectURL(image)
                        };
                    }));
                    setImages(convertedImages);
                    if (onChange) {
                        // Devolver el array de { file, base64 }
                        const imagesToReturn = convertedImages.map(img => ({
                            file: img.file,
                            base64: img.base64
                        }));
                        onChange(imagesToReturn, event);
                    }
                } else {
                    const imagesWithPreview = updatedImages.map(image => {
                        // Si la imagen ya tiene preview, la mantenemos
                        if (image.preview) {
                            return image;
                        }
                        // Si es una nueva imagen, creamos el preview
                        return {
                            file: image, // Guardamos el File puro en la propiedad file
                            preview: URL.createObjectURL(image)
                        };
                    });
                    setImages(imagesWithPreview);
                    if (onChange) {
                        // Devolver el array de File puro
                        const imagesToReturn = imagesWithPreview.map(img => img.file);
                        onChange(imagesToReturn, event);
                    }
                }
            } catch (error) {
                console.error('Error processing images:', error);
                newErrors.push('Error al procesar las imágenes');
                setErrors(newErrors);
            }
        }
    };

    const removeImage = (index) => {
        const updatedImages = [...images];
        URL.revokeObjectURL(updatedImages[index].preview);
        updatedImages.splice(index, 1);
        setImages(updatedImages);
        if (onChange) {
            // Devolver el array de File puro o { file, base64 }
            const imagesToReturn = base64
                ? updatedImages.map(img => ({ file: img.file, base64: img.base64 }))
                : updatedImages.map(img => img.file);
            onChange(imagesToReturn, null);
        }
    };

    const clearImages = () => {
        images.forEach(image => URL.revokeObjectURL(image.preview));
        setImages([]);
        if (onChange) {
            onChange([], null);
        }
    };

    // Cleanup preview URLs when component unmounts
    useEffect(() => {
        return () => {
            images.forEach(image => URL.revokeObjectURL(image.preview));
        };
    }, [images]);

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    width: '100%',
                    border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: '8px',
                    padding: 0.5,
                    textAlign: 'center',
                    backgroundColor: isDragActive 
                        ? theme.palette.mode === 'dark' 
                            ? 'rgba(120, 120, 255, 0.1)' 
                            : 'rgba(25, 118, 210, 0.05)'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    ...sx
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    multiple={multiple}
                    accept="image/*"
                />
                
                {images.length === 0 ? (
                    <>
                        <CloudUploadIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {dropzoneText}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {multiple ? `Puedes subir hasta ${maxFiles} imágenes` : 'Solo una imagen permitida'}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                fileInputRef.current.click();
                            }}
                        >
                            {buttonText}
                        </Button>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: multiple ? 'repeat(auto-fill, minmax(120px, 1fr))' : '1fr',
                        gap: 2,
                        width: '100%',
                        p: 1
                    }}>
                        {images.map((image, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    aspectRatio: '1',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <img
                                    src={image.preview}
                                    alt={image.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeImage(index);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {multiple && images.length < maxFiles && (
                            <Box
                                sx={{
                                    aspectRatio: '1',
                                    border: `1px dashed ${theme.palette.divider}`,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                    }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    fileInputRef.current.click();
                                }}
                            >
                                <CloudUploadIcon sx={{ fontSize: 30, color: theme.palette.primary.main }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            
            {/* Error messages */}
            {errors.length > 0 && (
                <Box sx={{ mt: 2, color: theme.palette.error.main }}>
                    {errors.map((error, index) => (
                        <Typography key={index} variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center' }}>
                            <WarningIcon sx={{ mr: 1, fontSize: 16 }} />
                            {error}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}